import { animate, group, query, style, transition, trigger } from "@angular/animations";
import { ConfigurableFocusTrap, ConfigurableFocusTrapFactory, LiveAnnouncer } from "@angular/cdk/a11y";
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  ViewChild,
} from "@angular/core";
import { firstValueFrom } from "rxjs";
import { FADE_TIME, SLIDE_ANIMATION } from "src/app/animations";
import { FmsService } from "src/app/services/fms.service";
import { SimulationAgeComponent } from "../../simulation-age/simulation-age.component";
import { FmsPipe } from "../../../../shared/pipes/fms.pipe";
import { ButtonModule } from "@storeblocks/button-ng";

enum AnnouncementKey {
  Open = "simulationParamsActivator.announcement.open",
  Close = "simulationParamsActivator.announcement.close",
}

const BACKDROP_SELECTOR = ".backdrop";
const PANEL_SELECTOR = ".panel";

@Component({
  selector: "app-simulation-params-panel",
  templateUrl: "./simulation-params-panel.component.html",
  styleUrls: ["./simulation-params-panel.component.scss"],
  animations: [
    trigger("slideUpDown", [
      transition(":enter", [
        group([
          query(BACKDROP_SELECTOR, [style({ opacity: 0 }), animate(FADE_TIME, style({ opacity: 1 }))]),
          query(PANEL_SELECTOR, [
            style({ transform: "translateY(100%)" }),
            animate(SLIDE_ANIMATION, style({ transform: "translateY(0)" })),
          ]),
        ]),
      ]),
      transition(":leave", [
        group([
          query(BACKDROP_SELECTOR, [animate(FADE_TIME, style({ opacity: 0 }))]),
          query(PANEL_SELECTOR, [animate(SLIDE_ANIMATION, style({ transform: "translateY(100%)" }))]),
        ]),
      ]),
    ]),
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [SimulationAgeComponent, FmsPipe, ButtonModule],
})
export class SimulationParamsPanelComponent implements OnDestroy {
  @ViewChild("panelRef")
  public panelRef!: ElementRef;

  @Input()
  public open = false;
  @Output()
  public readonly close = new EventEmitter<void>();
  @Output()
  public readonly closed = new EventEmitter<void>();

  private focusTrap!: ConfigurableFocusTrap;

  constructor(
    private readonly focusTrapFactory: ConfigurableFocusTrapFactory,
    private readonly fmsService: FmsService,
    private readonly liveAnnouncer: LiveAnnouncer,
  ) {}

  public ngOnDestroy(): void {
    this.liveAnnouncer.clear();

    if (this.focusTrap) {
      this.focusTrap.destroy();
    }
  }

  public onAnimationEvent(): void {
    if (this.open) {
      this.focusTrap = this.focusTrapFactory.create(this.panelRef.nativeElement);
    } else {
      this.closed.emit();
    }
  }

  public async announce(): Promise<void> {
    const openText = await firstValueFrom(this.fmsService.translateAsync(AnnouncementKey.Open));
    const closeText = await firstValueFrom(this.fmsService.translateAsync(AnnouncementKey.Close));

    return this.liveAnnouncer.announce(this.open ? openText : closeText);
  }
}
